import type { ProgramPublic } from "@/client"
import { Checkbox } from "@/components/ui/checkbox"
import {
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Badge, Box, Button, HStack, Stack, Text } from "@chakra-ui/react"
import { useState } from "react"

export interface DashboardProgramFilterProps {
  programs: ProgramPublic[]
  selectedProgramIds: string[]
  onChange: (programIds: string[]) => void
  isLoading?: boolean
}

export function DashboardProgramFilter({
  programs,
  selectedProgramIds,
  onChange,
  isLoading,
}: DashboardProgramFilterProps) {
  const [isOpen, setIsOpen] = useState(false)
  const selected = new Set(selectedProgramIds)
  const triggerLabel =
    selectedProgramIds.length === 0
      ? "All programs"
      : `${selectedProgramIds.length} programs`

  function toggleProgram(programId: string) {
    const next = new Set(selected)
    if (next.has(programId)) {
      next.delete(programId)
    } else {
      next.add(programId)
    }
    onChange(Array.from(next))
  }

  return (
    <DialogRoot open={isOpen} onOpenChange={({ open }) => setIsOpen(open)}>
      <DialogTrigger asChild>
        <Button variant="outline" loading={isLoading} aria-label={triggerLabel}>
          {triggerLabel}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Select programs</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <Stack gap={2}>
            {programs.map((program) => (
              <Box
                key={program.id}
                borderWidth="1px"
                borderRadius="md"
                px={3}
                py={2}
              >
                <Checkbox
                  checked={selected.has(program.id)}
                  onCheckedChange={() => toggleProgram(program.id)}
                >
                  <HStack justify="space-between" w="full" gap={3}>
                    <Stack gap={0}>
                      <Text>{program.name}</Text>
                      {program.description ? (
                        <Text color="fg.muted" fontSize="sm">
                          {program.description}
                        </Text>
                      ) : null}
                    </Stack>
                    {program.enrolled_count != null ? (
                      <Badge variant="subtle">{program.enrolled_count}</Badge>
                    ) : null}
                  </HStack>
                </Checkbox>
              </Box>
            ))}
          </Stack>
        </DialogBody>
      </DialogContent>
    </DialogRoot>
  )
}
